import { useEffect, useState } from "react";
import { AlertTriangle, Smartphone } from "lucide-react";
import { publicApi, fmtMoney } from "@/api";

export default function PagoStep({ emit, cobertura, importante, moto, titular }) {
  const [estado, setEstado] = useState(null);
  const [redirigiendo, setRedirigiendo] = useState(false);
  const [pagando, setPagando] = useState(false);
  const esRobo = cobertura?.tipo === "robo";

  // Pide un link de MP FRESCO en cada intento: el link de ATM queda "usado" si el cliente
  // entro a MercadoPago y salio sin pagar (sin fondos), y con el viejo le da error al volver.
  const irAPagar = async (e) => {
    e.preventDefault();
    setRedirigiendo(true);
    setPagando(true);
    const win = window.open("", "_blank"); // abrir ya, en el gesto del usuario (evita popup-blocker)
    let url = emit.linkpago || estado?.linkpago;
    try {
      const { data } = await publicApi.post(`/public/operacion/${emit.operacion_id}/link-pago`);
      if (data?.linkpago) url = data.linkpago;
    } catch { /* si falla, usa el link que ya tenia */ }
    setPagando(false);
    if (win) win.location.href = url; else window.open(url, "_blank", "noopener");
  };

  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const { data } = await publicApi.get(`/public/operacion/${emit.operacion_id}`);
        setEstado(data);
        timer = setTimeout(poll, 10000);
      } catch {
        timer = setTimeout(poll, 15000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [emit.operacion_id]);

  return (
    <div data-testid="step-pago" className="pt-2">
      <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Último paso: el pago</h1>
      <p className="text-slate-500 mb-6">Pagá con MercadoPago para activar tu cobertura.</p>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="pago-detalle">
        <div className="flex justify-between items-start mb-3">
          <p className="font-heading font-bold text-slate-900">{cobertura?.nombre_visible || cobertura?.descripcion}</p>
          <img src="/api/uploads/atm-logo.png" alt="Emitido por ATM Seguros" className="h-8 w-auto" data-testid="pago-atm-logo" />
        </div>
        <div className="space-y-1.5 text-sm border-b border-slate-100 pb-3 mb-3">
          <div className="flex justify-between"><span className="text-slate-500">Moto</span><span className="font-medium text-slate-800 text-right" data-testid="pago-moto">{moto?.marca_desc} {moto?.modelo_desc} {moto?.anio}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Patente</span><span className="font-data text-slate-800" data-testid="pago-patente">{titular?.patente === "A/D" ? "A/D (a declarar)" : titular?.patente}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Titular</span><span className="font-medium text-slate-800 text-right" data-testid="pago-titular">{titular?.apellido}, {titular?.nombre}</span></div>
        </div>
        <div className="flex items-center gap-2">
          <p className="font-heading text-3xl font-bold text-slate-900">{fmtMoney(cobertura?.impcuotas)}<span className="text-base font-normal text-slate-500">/mes</span></p>
          <span className="inline-flex items-center rounded-md bg-[#009EE3] px-2 py-1.5" title="Pagás con Mercado Pago">
            <img src="/assets/mp-logo.png" alt="Mercado Pago" className="h-6 w-auto" data-testid="pago-mp-logo" />
          </span>
        </div>
        {emit.solicitud && <p className="text-xs text-slate-400 mt-2 font-data">Solicitud N° {emit.solicitud}</p>}
      </div>

      {esRobo && importante && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 mb-6 flex gap-3" data-testid="inspeccion-alert">
          <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-orange-800 text-sm mb-1">Inspección de tu moto</p>
            <p className="text-sm text-orange-800">{importante}</p>
          </div>
        </div>
      )}

      <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 mb-6 flex gap-3" data-testid="pago-postpago-info">
        <Smartphone className="w-6 h-6 text-sky-700 shrink-0 mt-0.5" />
        <p className="text-base font-medium text-sky-900 leading-relaxed">Luego del pago ingresá a ATM Seguros, descargá la app, creá tu cuenta y vas a poder acceder a tu póliza.</p>
      </div>

      <div className="space-y-3">
        {emit.linkpago || estado?.linkpago ? (
          <>
            <button type="button" data-testid="mercadopago-button" onClick={irAPagar} disabled={pagando}
              className="w-full h-14 rounded-full bg-mp text-white font-heading font-semibold text-lg flex items-center justify-center gap-2 transition shadow-lg disabled:opacity-60">
              <img src="/assets/mp-logo.png" alt="Mercado Pago" className="h-7 w-auto" data-testid="mp-logo-img" /> {pagando ? "Generando link de pago..." : "Pagar con MercadoPago"}
            </button>
            {redirigiendo && (
              <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center" data-testid="mp-redirigiendo">
                Direccionando a MercadoPago... Te abrimos el pago en una pestaña nueva. Si no se abrió, tocá el botón de nuevo.
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500 bg-slate-100 rounded-xl p-3" data-testid="linkpago-pending">El link de pago se está generando. Actualizamos esta pantalla automáticamente.</p>
        )}
        {(emit.linkpago || estado?.linkpago) && (
          <div className="mt-2 bg-slate-50 border border-slate-200 rounded-2xl p-4 text-center" data-testid="pago-link-verificar">
            <p className="text-xs text-slate-500 mb-1.5">Tu link de pago oficial de ATM Seguros. Verificá que empiece con <b className="text-slate-700">atm.ar/</b> antes de pagar:</p>
            <a href={emit.linkpago || estado?.linkpago} target="_blank" rel="noopener noreferrer"
              className="text-sm font-data text-emerald-700 break-all underline underline-offset-2" data-testid="pago-link-texto">
              {emit.linkpago || estado?.linkpago}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
